'use client';

import { useEffect, useMemo, useState } from 'react';
import { useGetRouteDetailsQuery, useGetRouteStopsQuery } from 'src/api/redux';
import { captureException } from 'src/observability';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectRouteNavigationHydrated,
  selectRouteRoundTrip,
} from 'src/redux/slices/routeNavigationSlice';
import type { EnrichedRouteStop, RouteDirections } from './types';
import useRouteDirections from './useRouteDirections';
import { buildDirectionsPoints, enrichRouteStops } from './utils';

export interface UseRoutePreviewReturn {
  /** The route's stops, for the map markers */
  stops: EnrichedRouteStop[];
  /** The driving path through the stops; null until loaded or when unavailable */
  directions: RouteDirections | null;
  /** The route or its stops couldn't be loaded (deleted, or not the user's) */
  isError: boolean;
}

/**
 * Loads a saved route's stops and driving path for drawing on a map, without
 * the routes drawer. Pass null for no preview. The path starts at the route's
 * saved origin (when it has one) and returns there for round trips, as the
 * route detail panel's does.
 */
const useRoutePreview = (routeId: string | null): UseRoutePreviewReturn => {
  // `currentData`, not `data`: `data` keeps the last result after the query is
  // skipped or its route changes, which would leave a cleared or replaced
  // route's markers on the map.
  const { currentData: route, isError: isRouteError } = useGetRouteDetailsQuery(
    routeId || '',
    { skip: !routeId }
  );
  const { currentData: stopsData, isError: isStopsError } =
    useGetRouteStopsQuery(routeId || '', { skip: !routeId });
  const roundTrip = useAppSelector((state) =>
    routeId ? selectRouteRoundTrip(state, Number(routeId)) : false
  );
  // The round-trip choice is persisted; before hydration it reads false, which
  // would draw a one-way path and then redraw it with the return leg.
  const isNavigationHydrated = useAppSelector(selectRouteNavigationHydrated);
  const { fetchDirections } = useRouteDirections();
  const [directions, setDirections] = useState<RouteDirections | null>(null);

  const apiStops = useMemo(
    () => [...(stopsData ?? [])].sort((a, b) => a.position - b.position),
    [stopsData]
  );
  const stops = useMemo(() => enrichRouteStops(apiStops), [apiStops]);

  const hasRoute = !!route;
  const originLat = route?.origin_lat ?? null;
  const originLng = route?.origin_lng ?? null;

  useEffect(() => {
    // Drop the previous route's path while the replacement loads
    setDirections(null);

    // The saved origin and return leg come from the route's details and the
    // persisted round-trip choice, so wait for both rather than drawing a path
    // that leaves them out.
    if (!routeId || !hasRoute || !isNavigationHydrated) {
      return;
    }

    const startingPoint =
      originLat !== null && originLng !== null
        ? { lat: originLat, lng: originLng }
        : null;
    const points = buildDirectionsPoints(apiStops, startingPoint, roundTrip);
    // A saved origin alone (or twice, on a round trip) is not a route
    if (apiStops.length === 0 || points.length < 2) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    fetchDirections(points, { signal: controller.signal })
      .then((result) => {
        if (!cancelled) setDirections(result);
      })
      .catch((error) => {
        if (!cancelled) {
          captureException(error, { tags: { context: 'route-preview' } });
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    routeId,
    hasRoute,
    isNavigationHydrated,
    apiStops,
    originLat,
    originLng,
    roundTrip,
    fetchDirections,
  ]);

  return {
    stops,
    directions,
    isError: !!routeId && (isStopsError || isRouteError),
  };
};

export default useRoutePreview;
